import { useEffect, useState } from "react";
import { ShieldCheck, UserPlus, X } from "lucide-react";
import { Field, TextInput, SelectInput } from "@/components/brand/Field";
import { api, formatApiError } from "@/lib/api";

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", role: "editor" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((err) => setError(formatApiError(err.response?.data?.detail)));
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    try {
      await api.post("/admin/users", f);
      setShowForm(false);
      setF({ name: "", email: "", password: "", role: "editor" });
      setNotice("User created.");
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const deactivate = async (u) => {
    setError("");
    try {
      await api.post(`/admin/users/${u.id}/deactivate`);
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="admin-users">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="space-y-1">
          <h1 className="font-display text-2xl font-bold text-brand-snow sm:text-3xl">Admin users</h1>
          <p className="text-sm text-brand-mist">
            Admins manage everything. Editors can manage content — articles, pages, FAQs and portfolio — but never enquiries, settings, credentials or users.
          </p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="user-new" className="inline-flex items-center gap-2 rounded-full bg-brand-emerald px-5 py-2.5 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow">
          <UserPlus size={15} /> Add user
        </button>
      </div>
      {notice && <p className="flex items-center gap-2 text-sm text-brand-emerald" data-testid="users-notice"><ShieldCheck size={15} /> {notice}</p>}
      {error && <p role="alert" className="text-sm text-red-400" data-testid="users-error">{error}</p>}

      <div className="surface-card divide-y divide-brand-mist/10" data-testid="users-list">
        {users.map((u) => (
          <div key={u.id} className="flex items-center justify-between gap-4 px-6 py-4" data-testid={`user-row-${u.email}`}>
            <div>
              <p className="text-sm font-medium text-brand-snow">{u.name} {me?.id === u.id && <span className="text-xs text-brand-mist">(you)</span>}</p>
              <p className="text-xs text-brand-mist">{u.email}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`rounded-full border px-3 py-1 text-[11px] font-medium ${u.role === "admin" ? "border-brand-emerald/40 text-brand-emerald" : "border-brand-azure/40 text-brand-azure"}`} data-testid={`user-role-${u.email}`}>
                {u.role}
              </span>
              {u.active === false ? (
                <span className="text-xs text-brand-mist">deactivated</span>
              ) : me?.id !== u.id ? (
                <button onClick={() => deactivate(u)} data-testid={`user-deactivate-${u.email}`} className="rounded-full border border-brand-mist/25 px-3 py-1.5 text-xs text-brand-mist hover:border-red-400 hover:text-red-400">
                  Deactivate
                </button>
              ) : null}
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex justify-center bg-brand-black/70 backdrop-blur-sm sm:p-6" onClick={() => setShowForm(false)} data-testid="user-form-modal">
          <form onSubmit={add} className="h-full w-full max-w-md space-y-5 overflow-y-auto border border-brand-mist/15 bg-brand-charcoal p-6 sm:h-auto sm:rounded-2xl sm:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-brand-snow">Add admin user</h2>
              <button type="button" onClick={() => setShowForm(false)} aria-label="Close" className="rounded-md p-2 text-brand-mist hover:text-brand-snow"><X size={18} /></button>
            </div>
            <Field label="Name" htmlFor="nu-name" required><TextInput id="nu-name" testid="nu-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></Field>
            <Field label="Email" htmlFor="nu-email" required><TextInput id="nu-email" type="email" testid="nu-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required /></Field>
            <Field label="Temporary password" htmlFor="nu-password" required hint="Minimum 10 characters. Ask the user to change it after first sign-in.">
              <TextInput id="nu-password" type="password" testid="nu-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} minLength={10} required />
            </Field>
            <Field label="Role" htmlFor="nu-role">
              <SelectInput id="nu-role" testid="nu-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
                <option value="editor">Editor — content only</option>
                <option value="admin">Admin — full access</option>
              </SelectInput>
            </Field>
            <button type="submit" data-testid="nu-save" className="rounded-full bg-brand-emerald px-6 py-3 font-display text-sm font-semibold text-brand-black hover:bg-brand-snow">Create user</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
